import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { DevicePhoneMobileIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';

import { Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { useAnalytics } from '@restworld/utils-common';

import {
  candidateModalVariantAtom,
  useCandidateStateChangeModal,
  candidateStateChangeSelectionAtom,
  selectedJobApplicationAtom,
  isCandidateHiredSuccessfullyAtom,
  isCandidateModalSuccessScreenAtom,
  kanbanMoveSucceededAtom,
} from '../../../../employer-job-listing.atoms';
import useUpdateJobApplication, {
  useUpdateDiscardedJobApplication,
} from '../../../../hooks/use-update-job-application';
import useGetOrganizationPerformanceMetricsQuery from '../../../../../organisations/hooks/use-get-organization-performance-metrics-query';

interface StatusChangeConfirmationProps {
  workerId: string;
  jobPositionId: string;
}
export const CandidateStateChangeConfirmation = ({ workerId, jobPositionId }: StatusChangeConfirmationProps) => {
  const { t } = useTranslation('job-positions-page');
  const { mutate, isSuccess, isPending } = useUpdateJobApplication();
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const [variant] = useAtom(candidateModalVariantAtom);
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const [, setIsCandidateHiredSuccessfullyAtom] = useAtom(isCandidateHiredSuccessfullyAtom);
  const [, setIsCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);
  const [, setKanbanMoveSucceeded] = useAtom(kanbanMoveSucceededAtom);
  const candidateName = selectedJobApplication?.worker?.name ?? '';
  const employerName = user?.name ?? '';

  const {
    mutate: mutateDiscardApplication,
    isSuccess: isSuccessMutateDiscardApplication,
    isPending: isPendingMutateDiscardApplication,
  } = useUpdateDiscardedJobApplication();
  const statusKey = useAtomValue(candidateStateChangeSelectionAtom);
  const { data: performanceMetrics } = useGetOrganizationPerformanceMetricsQuery({
    enabled: statusKey === 'worker_jp_hired',
  });

  const defaultText = useMemo(
    () =>
      statusKey === 'worker_jp_hired'
        ? `${t('do_you_confirm_name_has_been_hired', { name: candidateName })}?`
        : statusKey === 'worker_jp_stage'
        ? t('you_are_moving_name_in_candidates_in_stage_or_negotiation', { name: candidateName })
        : t('you_are_moving_name_in_candidates_to_be_called', { name: candidateName }),
    [candidateName, statusKey, t]
  );

  const successText = useMemo(
    () =>
      statusKey === 'worker_jp_hired' ? (
        <span>
          {t('congratulations')} {employerName}!<br />
          {t('this_is_your_x_hire_with_restworld', { count: performanceMetrics?.hired_workers })}
        </span>
      ) : statusKey === 'worker_jp_stage' ? (
        `${t('you_moved_name_in_stage_or_negotiation', { name: candidateName })}.`
      ) : (
        t('you_moved_name_in_candidates_to_be_called', { name: candidateName })
      ),
    [candidateName, employerName, performanceMetrics?.hired_workers, statusKey, t]
  );

  const handleSubmit = useCallback(() => {
    const params = {
      params: {
        status_key: statusKey !== '' ? statusKey : null,
      },
      workerId,
      jobPositionId,
    };

    if (
      (statusKey !== 'worker_jp_employer_interested' &&
        statusKey !== 'worker_jp_stage' &&
        statusKey !== 'worker_jp_hired') ||
      variant === 'worker_jp_recovery_confirmation'
    ) {
      mutateDiscardApplication(params);
      return;
    }
    mutate(params);
  }, [jobPositionId, mutate, mutateDiscardApplication, statusKey, variant, workerId]);

  useEffect(() => {
    if (statusKey === 'worker_jp_hired' && isSuccess) {
      setIsCandidateHiredSuccessfullyAtom(true);
    }
    if (isSuccess) setIsCandidateModalSuccessScreen(true);
  }, [isSuccess, setIsCandidateHiredSuccessfullyAtom, setIsCandidateModalSuccessScreen, statusKey]);

  // Conversion metric for the preselection-candidates A/B: a candidate entering
  // the first kanban column (`worker_jp_employer_interested`). Fires once on the
  // successful move; PostHog auto-tags it with the experiment variant.
  useEffect(() => {
    if (isSuccess && statusKey === 'worker_jp_employer_interested') {
      trackEvent('employer_preselection_candidate_moved_to_kanban', {
        job_position_id: jobPositionId,
        worker_id: workerId,
      });
      // Signals the job-position page to run the one-time "Le tue scelte"
      // intro (tab switch + tooltip) once the modal closes.
      setKanbanMoveSucceeded(true);
    }
  }, [isSuccess, statusKey, jobPositionId, workerId, trackEvent, setKanbanMoveSucceeded]);

  return (
    <CandidateModalStatusChangeScreen
      topIcon={
        statusKey === 'worker_jp_stage' ? (
          <ClipboardDocumentCheckIcon className="h-6 w-6 text-neutral-700" />
        ) : statusKey === 'worker_jp_hired' && isSuccess ? (
          '🎉🎉🎉'
        ) : statusKey === 'worker_jp_hired' ? null : (
          <DevicePhoneMobileIcon className="h-6 w-6 text-neutral-700" />
        )
      }
      successText={successText}
      defaultText={defaultText}
      isCompletedSuccessfully={isSuccess || isSuccessMutateDiscardApplication}
      isLoading={isPending || isPendingMutateDiscardApplication}
      handleSubmit={handleSubmit}
      hideBottomText={statusKey === 'worker_jp_hired' && !isSuccess}
      subtext={
        statusKey === 'worker_jp_hired' && isSuccess ? `${t('let_us_know_how_it_goes_and_if_you_need_more_help')}!` : ''
      }
      hasPurpleCta={statusKey === 'worker_jp_hired' && isSuccess}
    />
  );
};

export function CandidateModalStatusChangeScreen({
  handleSubmit,
  isCompletedSuccessfully,
  isLoading,
  successText,
  defaultText,
  subtext,
  topIcon,
  hideBottomText,
  hasPurpleCta,
  bottomConfirmationText,
}: CandidateModalStatusChangeScreenProps) {
  const { t } = useTranslation('job-positions-page');
  const { closeCandidateModal } = useCandidateStateChangeModal();
  const [isCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);
  const [, setCandidateModalVariant] = useAtom(candidateModalVariantAtom);

  const handleCancel = useCallback(() => setCandidateModalVariant('default'), [setCandidateModalVariant]);

  return (
    <div className="flex flex-col justify-between h-full relative">
      <ContentWrapper isSuccess={isCandidateModalSuccessScreen}>
        <>
          <div className="self-stretch flex-col justify-start items-center gap-5 flex">
            {topIcon}
            <Heading align="center" color="neutral">
              {isCompletedSuccessfully ? successText : defaultText}
            </Heading>
            {subtext && (
              <Heading size={5} weight="medium" color="muted">
                {subtext}
              </Heading>
            )}
            {hideBottomText ? null : isCompletedSuccessfully ? (
              <ButtonV2
                onClick={() => {
                  handleCancel();
                  closeCandidateModal();
                }}
                variant={hasPurpleCta ? 'secondary-restworld' : 'primary'}
              >
                {t('close')}
              </ButtonV2>
            ) : (
              <Heading size={5} weight="medium" color="muted" align="center">
                {bottomConfirmationText ?? `${t('do_you_confirm_your_choice')}?`}
              </Heading>
            )}
          </div>
        </>
      </ContentWrapper>

      {!isCompletedSuccessfully && (
        <div className="justify-between gap-[15px] inline-flex px-4 py-4  border-neutral-40 lg:absolute lg:bottom-0 lg:-left-10 lg:-right-10 lg:border-t lg:px-10">
          <ButtonV2 type="button" variant="tertiary" onClick={handleCancel}>
            {t('cancel')}
          </ButtonV2>
          <ButtonV2 loading={isLoading} onClick={handleSubmit} className={'min-h-fit'}>
            {t('confirm')}
          </ButtonV2>
        </div>
      )}
    </div>
  );
}

interface CandidateModalStatusChangeScreenProps {
  isCompletedSuccessfully?: boolean;
  isLoading?: boolean;
  handleSubmit?: () => void;
  defaultText?: string;
  successText?: React.ReactNode;
  subtext?: string;
  topIcon?: React.ReactNode;
  hideBottomText?: boolean;
  hasPurpleCta?: boolean;
  bottomConfirmationText?: React.ReactNode;
}

const ContentWrapper = ({
  isSuccess,
  className,
  ...props
}: { isSuccess?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex-col justify-center items-center inline-flex h-full lg:relative',
      !isSuccess && 'lg:-top-[80px]',
      className
    )}
    {...props}
  />
);
